import {
  Button,
  Checkbox,
  Dropdown,
  Field,
  Input,
  makeStyles,
  tokens,
  Option,
  RadioGroup,
  Radio,
  RadioGroupOnChangeData,
  Text,
  Spinner,
  mergeClasses,
} from '@fluentui/react-components';
import { AddRegular } from '@fluentui/react-icons';
import { uniq } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { userContextSlice } from '@tdp/common/auth';
import { AppDetailsHeader } from '@tdp/common/components/AppDetailsHeader';
import { LoadingStates } from '@tdp/common/constants';
import { makeDocsLink } from '@tdp/common/constants';
import { InputSection } from '@tdp/common/detailsPage';
import { Flex, StyledText } from '@tdp/common/fluentV9MigrationComponents';
import { useCommand, useSelector } from '@tdp/core';
import { botSlice } from '@tdp/modules/bots';

import { AppFeaturesBotDataGrid } from './AppFeaturesBotDataGrid';
import { AppFeaturesBotDialog } from './AppFeaturesBotDialog';
import { IAppFeaturesBotDataGrid } from './IAppFeaturesBotDataGrid';
import { createAppZoomHook } from '../../../hooks';
import { GroupChoiceKey, onActivate } from '../AppFeaturesCommon/AppBotSelectUtils';
import { appFeaturesCommonStyles } from '../AppFeaturesCommon/appFeaturesCommon.styles';
import { AppFeaturesEmpty } from '../AppFeaturesCommon/AppFeaturesEmpty';
import { createAppFeaturesFooter } from '../AppFeaturesCommon/AppFeaturesFooter';

const useStyles = makeStyles({
  radioFullWidth: {
    '& .fui-Radio__label': {
      width: '100%',
    },
  },
  spaced: {
    rowGap: '20px',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    '& .fui-Radio': {
      width: '100%',
    },
  },
  section: {
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalM,
    maxWidth: '700px',
  },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalS,
  },
  button: {
    marginTop: tokens.spacingVerticalXL,
    maxWidth: '300px',
    justifyContent: 'flex-start',
  },
  dropdown: {
    marginTop: '5px',
    marginBottom: '10px',
    marginLeft: '0',
    marginRight: '0',
  },
  input: {
    marginTop: '5px',
    marginLeft: '25px',
    marginBottom: '0',
    marginRight: '0',
  },
  inlineLink: {
    width: 'fit-content',
    marginTop: '0',
    marginRight: '0',
  },
  link: {
    cursor: 'pointer',
    color: tokens.colorBrandForegroundLink, // Fluent UI token for link color
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
      color: tokens.colorBrandForegroundLinkHover, // Fluent UI token for link hover color
    },
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    marginTop: tokens.spacingVerticalXXS,
  },
});

const useBots = createAppZoomHook((app) => app.bots);
const Footer = createAppFeaturesFooter((app) => [app.bots]);

export const AppFeaturesBot: React.FC = () => {
  const navigate = useNavigate();
  const baseStyles = appFeaturesCommonStyles();
  const classes = useStyles();
  const intl = useIntl();
  const appId = useParams<{ appId: string }>().appId ?? '';
  // List of all the bots in the system
  const botList = useSelector(botSlice.selectors.selectAll);
  const botListFetchStatus = useSelector(botSlice.selectors.botListApiStatus);
  const loadBots = useCommand((commands) => commands.botCommands.loadBots);
  const [selectedBot, setSelectedBot] = useState<string>('');
  const isMicrosoftTenant = !!useSelector(
    userContextSlice.selectors.isMicrosoftTenant
  );
  // Bots data associated with the app
  const bots = useBots(appId);
  const [currentRadioGroup, setCurrentRadioGroup] = useState<GroupChoiceKey>(
    GroupChoiceKey.SelectFromExistingBots
  );

  const options = React.useMemo(
    () =>
      botList.map((bot) => ({
        key: bot.botId,
        text: `${bot.name} (${bot.botId})`,
      })),
    [botList]
  );
  useEffect(() => {
    if (botListFetchStatus === LoadingStates.NOT_STARTED) {
      loadBots();
    }
  }, []);

  useEffect(() => {
    if (botListFetchStatus === LoadingStates.SUCCESS && bots.base?.[0]) {
      const selectedBotList = botList.filter(
        (bot) => bot.botId === bots.base?.[0].botId
      );
      if (selectedBotList.length > 0) {
        setSelectedBot(bots.base[0].botId);
        setCurrentRadioGroup(GroupChoiceKey.SelectFromExistingBots);
      } else {
        setCurrentRadioGroup(GroupChoiceKey.ConnectToDifferentBot);
      }
    }
  }, [botListFetchStatus, bots.base]);

  const handleRevert = React.useCallback(() => {
    const selectedBotList = botList.filter(
      (bot) => bot.botId === bots.base?.[0]?.botId
    );
    if (selectedBotList.length > 0) {
      setSelectedBot(bots.base?.[0].botId ?? "");
      setCurrentRadioGroup(GroupChoiceKey.SelectFromExistingBots);
    } else {
      setCurrentRadioGroup(GroupChoiceKey.ConnectToDifferentBot);
    }
  }, [botList, bots.base, setSelectedBot, setCurrentRadioGroup]);

  const isBotAdded = bots.current?.[0]?.botId;
  const commands = [
    ...(bots.current?.[0]?.personalCommands ?? []).map((i) => ({
      id: `${i.title}-${i.description}`,
      title: i.title ?? '',
      description: i.description ?? '',
      scope: ['personal'],
    })),
    ...(bots.current?.[0]?.teamCommands ?? []).map((i) => ({
      id: `${i.title}-${i.description}`,
      title: i.title ?? '',
      description: i.description ?? '',
      scope: ['team'],
    })),
    ...(bots.current?.[0]?.groupChatCommands ?? []).map((i) => ({
      id: `${i.title}-${i.description}`,
      title: i.title ?? '',
      description: i.description ?? '',
      scope: ['groupChat'],
    })),
  ];

  const [isEdit, setIsEdit] = React.useState<IAppFeaturesBotDataGrid | null>(
    null
  );

  const onBotIdChanged = (val: string) => {
    bots.update((t) => [{ ...bots.current?.[0], botId: val }]);
  };

  const onScopeChanged = (str: string): void => {
    const scopes = [...(bots.current?.[0]?.scopes ?? [])];
    scopes.includes(str)
      ? scopes.splice(scopes.indexOf(str), 1)
      : scopes.push(str);
    bots.update((t) => [{ ...bots.current?.[0], scopes: scopes }]);
  };

  const onCommandChanged = (input: IAppFeaturesBotDataGrid) => {
    const current = { ...(bots.current?.[0] ?? {}) };
    current.personalCommands = current.personalCommands?.filter(
      (item) => `${item.title}-${item.description}` !== input.id
    );
    current.teamCommands = current.teamCommands?.filter(
      (item) => `${item.title}-${item.description}` !== input.id
    );
    current.groupChatCommands = current.groupChatCommands?.filter(
      (item) => `${item.title}-${item.description}` !== input.id
    );
    if (input.scope.includes('personal')) {
      current.personalCommands = [
        ...(current.personalCommands ?? []),
        { title: input.title, description: input.description },
      ];
    }
    if (input.scope.includes('team')) {
      current.teamCommands = [
        ...(current.teamCommands ?? []),
        { title: input.title, description: input.description },
      ];
    }
    if (input.scope.includes('groupChat')) {
      current.groupChatCommands = [
        ...(current.groupChatCommands ?? []),
        { title: input.title, description: input.description },
      ];
    }
    bots.update((t) => [{ ...current }]);
  };

  const onCommandsDelete = (input: IAppFeaturesBotDataGrid) => {
    const current = { ...(bots.current?.[0] ?? {}) };
    if (input.scope.includes('personal')) {
      current.personalCommands = current.personalCommands?.filter(
        (item) => `${item.title}-${item.description}` !== input.id
      );
    }
    if (input.scope.includes('team')) {
      current.teamCommands = current.teamCommands?.filter(
        (item) => `${item.title}-${item.description}` !== input.id
      );
    }
    if (input.scope.includes('groupChat')) {
      current.groupChatCommands = current.groupChatCommands?.filter(
        (item) => `${item.title}-${item.description}` !== input.id
      );
    }
    bots.update((t) => [{ ...current }]);
  };

  const isGuidError = () => {
    return !(bots.current?.[0]?.botId
      ? /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        bots.current[0]?.botId
      )
      : true);
  };

  const mergeCommandsById = () => {
    const map = new Map<string, IAppFeaturesBotDataGrid>();
    commands.forEach(({ id, scope, ...rest }) => {
      const ex = map.get(id);
      map.set(
        id,
        ex
          ? { ...ex, scope: uniq([...ex.scope, ...scope]) }
          : { ...rest, id, scope }
      );
    });
    return Array.from(map.values());
  };

  const setNewCommand = () => {
    setIsEdit({
      title: '',
      description: '',
      scope: [],
      id: '',
    });
  };

  const Empty = () => (
    <AppFeaturesEmpty
      onClick={() => {
        setNewCommand();
      }}
      buttonText={intl.formatMessage({
        defaultMessage: 'Create your first activity bot command',
        description: 'Button text to add a activity bot command',
      })}
    />
  );

  const onCreateBotHandler = (
    e: React.MouseEvent | React.KeyboardEvent
  ): void => {
    e.stopPropagation();
    navigate(`/tools/bots`);
  };

  // Event Handlers
  const handleBotGroupChange = (
    ev: React.FormEvent<HTMLDivElement>,
    data: RadioGroupOnChangeData
  ) => {
    setCurrentRadioGroup(data.value as GroupChoiceKey);
  };

  const selectFromExistingBots = intl.formatMessage({
    defaultMessage: 'Select an existing bot',
    description: 'Label for selecting from existing bots',
  });

  const connectToDifferentBot = intl.formatMessage({
    defaultMessage: 'Enter a bot ID',
    description: 'Label for connecting to a different bot',
  });

  return (
    <div className={baseStyles.wrapper}>
      {isEdit && (
        <AppFeaturesBotDialog
          item={isEdit}
          open={!!isEdit}
          setOpen={() => {
            setIsEdit(null);
          }}
          onUpdate={onCommandChanged}
        />
      )}
      <div className={baseStyles.container}>
        <AppDetailsHeader
          onBackNavigation={() => {
            navigate(-1);
          }}
          showBackNavigation
          title={
            <FormattedMessage
              defaultMessage="Bots"
              description={'Title for Bots Section'}
            />
          }
          description={
            <FormattedMessage
              defaultMessage="A conversational UI that can perform a set of tasks, reply to questions, and proactively send notifications in personal and shared contexts."
              description={'Description for Bots Section'}
            />
          }
          link={
            <FormattedMessage
              defaultMessage="Learn more about Bots"
              description={'Link to learn more about Bots Section'}
            />
          }
          href={makeDocsLink("microsoftteams/platform/concepts/bots/bots-overview")}
        />
        <br />
        <section className={classes.section}>
          <InputSection
            legend={<FormattedMessage defaultMessage="Identify your bot" />}
          >
            <Field id="botId">
              <RadioGroup
                layout="vertical"
                value={currentRadioGroup}
                onChange={handleBotGroupChange}
                className={classes.spaced}
              >
                <Radio
                  key={GroupChoiceKey.SelectFromExistingBots}
                  value={GroupChoiceKey.SelectFromExistingBots}
                  className={classes.radioFullWidth}
                  label={
                    <Flex column>
                      {selectFromExistingBots}
                      <Dropdown
                        className={classes.dropdown}
                        disabled={
                          currentRadioGroup !==
                          GroupChoiceKey.SelectFromExistingBots
                        }
                        aria-required={true}
                        aria-labelledby={'botId'}
                        selectedOptions={[selectedBot]}
                        onOptionSelect={(_, data) => {
                          setSelectedBot(data.optionValue ?? '');
                          onBotIdChanged(data.optionValue ?? '');
                        }}
                        value={selectedBot}
                        placeholder={botListFetchStatus === LoadingStates.LOADING
                          ? intl.formatMessage({ defaultMessage: 'Loading ...' })
                          : selectFromExistingBots}                      >
                        {botListFetchStatus === LoadingStates.LOADING ? (
                          <Option value="loading" disabled>
                            <Spinner
                              size="tiny"
                              label={intl.formatMessage({
                                defaultMessage: 'Loading ...',
                              })}
                            />
                          </Option>
                        ) : options.length === 0 ? (
                          <Option value="no-bots" disabled>
                            {
                              <>
                                <StyledText>
                                  {intl.formatMessage({
                                    defaultMessage: 'No bots found.',
                                    description: 'No bots found declaration',
                                  })}
                                </StyledText>{' '}
                                {!isMicrosoftTenant && (
                                  <Text
                                    className={classes.link}
                                    {...onActivate(onCreateBotHandler)}
                                  >
                                    {intl.formatMessage({
                                      description: 'create new bot label',
                                      defaultMessage: 'Create a new bot',
                                    })}
                                  </Text>
                                )}
                              </>
                            }
                          </Option>
                        ) : (
                          options.map((option) => (
                            <Option key={option.key} value={option.key}>
                              {option.text}
                            </Option>
                          ))
                        )}
                      </Dropdown>
                      {!isMicrosoftTenant && (
                        <Text
                          className={mergeClasses(
                            classes.inlineLink,
                            classes.link
                          )}
                          {...onActivate(onCreateBotHandler)}
                        >
                          {intl.formatMessage({
                            defaultMessage: 'Create a new bot',
                            description:
                              'Create a new bot using management tool',
                          })}
                        </Text>
                      )}
                    </Flex>
                  }
                />

                <Radio
                  key={GroupChoiceKey.ConnectToDifferentBot}
                  value={GroupChoiceKey.ConnectToDifferentBot}
                  className={classes.radioFullWidth}
                  label={
                    <Flex column>
                      {connectToDifferentBot}
                      <Input
                        disabled={
                          currentRadioGroup !==
                          GroupChoiceKey.ConnectToDifferentBot
                        }
                        type="text"
                        placeholder={'XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX'}
                        value={bots.current?.[0]?.botId ?? ''}
                        onChange={(_, data) => {
                          onBotIdChanged(data.value);
                        }}
                      />
                      {isGuidError() && (
                        <div className={classes.errorText}>
                          <FormattedMessage
                            defaultMessage="Bot Id must be a valid GUID"
                            description={'Error message for invalid bot id'}
                          />
                        </div>
                      )}
                    </Flex>
                  }
                />
              </RadioGroup>
            </Field>
          </InputSection>
        </section>
        <section className={classes.section}>
          <div className={classes.flexColumn}>
            <Field
              label={intl.formatMessage({
                defaultMessage: 'What can your bot do?',
              })}
            />
            <Checkbox
              label={intl.formatMessage({
                defaultMessage: 'Upload and download files',
              })}
              checked={bots.current?.[0]?.supportsFiles ?? false}
              disabled={!isBotAdded}
              onChange={(e, data) => {
                bots.update((t) => [
                  { ...bots.current?.[0], supportsFiles: !!data.checked },
                ]);
              }}
            />
            <Checkbox
              label={intl.formatMessage({
                defaultMessage: 'Only send notification',
              })}
              checked={bots.current?.[0]?.isNotificationOnly ?? false}
              disabled={!isBotAdded}
              onChange={(e, data) => {
                bots.update((t) => [
                  { ...bots.current?.[0], isNotificationOnly: !!data.checked },
                ]);
              }}
            />
            <Checkbox
              label={intl.formatMessage({
                defaultMessage: 'Supports audio calls',
              })}
              checked={bots.current?.[0]?.supportsCalling ?? false}
              disabled={!isBotAdded}
              onChange={(e, data) => {
                bots.update((t) => [
                  { ...bots.current?.[0], supportsCalling: !!data.checked },
                ]);
              }}
            />
            <Checkbox
              label={intl.formatMessage({
                defaultMessage: 'Supports video calls',
              })}
              checked={bots.current?.[0]?.supportsVideo ?? false}
              disabled={!isBotAdded}
              onChange={(e, data) => {
                bots.update((t) => [
                  { ...bots.current?.[0], supportsVideo: !!data.checked },
                ]);
              }}
            />
          </div>
        </section>
        <section className={classes.section}>
          <div className={classes.flexColumn}>
            <Field
              label={intl.formatMessage({
                defaultMessage:
                  'Select the scopes where people can use your bot',
              })}
            />
            <Checkbox
              label={intl.formatMessage({ defaultMessage: 'Personal' })}
              checked={bots.current?.[0]?.scopes?.includes('personal') ?? false}
              disabled={!isBotAdded}
              onChange={(e, data) => {
                onScopeChanged('personal');
              }}
            />
            <Checkbox
              label={intl.formatMessage({ defaultMessage: 'Team' })}
              checked={bots.current?.[0]?.scopes?.includes('team') ?? false}
              disabled={!isBotAdded}
              onChange={(e, data) => {
                onScopeChanged('team');
              }}
            />
            <Checkbox
              label={intl.formatMessage({ defaultMessage: 'Group chat' })}
              checked={
                bots.current?.[0]?.scopes?.includes('groupChat') ?? false
              }
              disabled={!isBotAdded}
              onChange={(e, data) => {
                onScopeChanged('groupChat');
              }}
            />
          </div>
        </section>
        {!bots.base?.[0]?.botId ? (
          <></>
        ) : commands.length == 0 ? (
          <Empty />
        ) : (
          <section className={classes.section}>
            <AppFeaturesBotDataGrid
              items={mergeCommandsById()}
              onEdit={(i: IAppFeaturesBotDataGrid) => {
                setIsEdit(i);
              }}
              onDelete={onCommandsDelete}
            />
            <Button
              className={classes.button}
              appearance="subtle"
              icon={<AddRegular />}
              onClick={() => {
                setNewCommand();
              }}
            >
              <FormattedMessage
                defaultMessage="Add a commands"
                description={'Button text to add a command'}
              />
            </Button>
          </section>
        )}
      </div>
      <Footer
        isError={!bots.current?.[0]?.botId || isGuidError()}
        onRevertCallback={handleRevert}
      />
    </div>
  );
};
